import {
  type HubDeletedExampleStream,
  type HubExampleStream,
  HubExampleStreamStatus,
  type HubResource,
} from '@robovision/hub'
import {
  addOrReplaceByKey,
  DateTemplates,
  formatDate,
  groupBy,
  HttpStatusCode,
  wait,
} from '@robovision/rvai-utils'
import type { AxiosHeaderValue } from 'axios'
import { saveAs } from 'file-saver'
import { defineStore } from 'pinia'
import {
  computed,
  reactive,
  shallowReadonly,
  toRefs,
} from 'vue'
import { ApiErrorId } from '~domain/ApiErrorId'
import { CameraConfiguration } from '~domain/cameras/CameraConfiguration'
import type { CameraConfigurationData } from '~domain/cameras/CameraConfigurationData'
import {
  CameraConfigurationCameraKind,
  CameraConfigurationKindIds,
} from '~domain/cameras/CameraConfigurationKind'
import { hubApi } from '~services/api'
import {
  acceptStatus,
  getResponseHeader,
  HttpHeaderName,
} from '~utils/api'
import { useExampleDefinitionsStore } from './useExampleDefinitionStore'


const DEFAULT_VALIDATION_LOG_FILE_EXTENSION = 'log'

function isValidConfigKind (kind?: string): boolean {
  return kind === CameraConfigurationKindIds.Camera || kind === CameraConfigurationKindIds.Datapool
}

export type CameraConfigurationFilter = `definitionId:${string}`

function parseFilter<S extends string> (filter: `${S}:${string}`): {
  field: S
  value: string
} {
  const parts = filter.split(':')

  if (parts.length !== 2) throw new TypeError(`Invalid filter: ${filter}`)

  const [field, value] = parts

  return {
    field: field as S,
    value: value ?? '',
  }
}

function matchesFilters (filters: CameraConfigurationFilter[], cameraConfiguration: CameraConfiguration): boolean {
  const parsedFilters = filters.map(parseFilter)
  const valuesPerField = groupBy(parsedFilters, 'field')
  return [...valuesPerField.entries()].every(
    ([field, fieldFilters]) => {
      const fieldValue = cameraConfiguration[field]
      return fieldFilters.some(f => fieldValue === f.value)
    },
  )
}

function getValidationLogFileExtension (contentDisposition: AxiosHeaderValue): string {
  if (typeof contentDisposition !== 'string') return DEFAULT_VALIDATION_LOG_FILE_EXTENSION

  const fileName = contentDisposition
    .split('filename=')
    .pop()

  if (!fileName) return DEFAULT_VALIDATION_LOG_FILE_EXTENSION

  return fileName
    .replaceAll('"', '')
    .split('.')
    .pop() || DEFAULT_VALIDATION_LOG_FILE_EXTENSION
}

/**
 * NOTE: This store does not support usage of multiple filtering options
 * for camera configurations. Only the latest loading filter will be applied.
 */
export const useCameraConfigurationsStore = defineStore('cameraConfigurations', () => {
  const state = reactive({
    cameraConfigurationsCalibrationResource: new Map<string, HubResource>(),
    downloading: false as boolean,
    lastLoadFilters: [] as CameraConfigurationFilter[],
    loading: false as boolean,
    /**
     * This state is limited to the current user session, so there is a chance
     * the stop validation request may be called multiple times,
     * but it's an acceptable limitation.
     */
    pendingStopValidations: new Set<CameraConfiguration['id']>(),
    saving: false as boolean,
    unfilteredCameraConfigurations: [] as CameraConfiguration[],
  })

  const filteredCameraConfigurations = computed(() => {
    const {
      lastLoadFilters,
      unfilteredCameraConfigurations,
    } = state

    return unfilteredCameraConfigurations.filter(c => matchesFilters(lastLoadFilters, c))
  })

  const { getExampleStreamViewTemplates } = useExampleDefinitionsStore()

  /**
   * Invariants:
   * - The camera configuration always has proper view templates
   * - pendingStopValidations contains only ids of camera configuration
   *   that are known to be in validating state
   */
  const invariantsKeeper = {
    onCameraConfigurationDelete (cameraConfigurationId: CameraConfiguration['id']) {
      state.pendingStopValidations.delete(cameraConfigurationId)
    },
    onCameraConfigurationUpdate (cameraConfiguration: CameraConfiguration) {
      cameraConfiguration.setViewTemplates(
        getExampleStreamViewTemplates(cameraConfiguration.definitionId),
      )

      if (cameraConfiguration.status !== HubExampleStreamStatus.VALIDATING) {
        state.pendingStopValidations.delete(cameraConfiguration.id)
      }
    },
    onManyCameraConfigurationsUpdate (cameraConfigurations: CameraConfiguration[]) {
      for (const cameraConfiguration of cameraConfigurations) {
        this.onCameraConfigurationUpdate(cameraConfiguration)
      }
    },
  }

  const actions = {
    async createCameraConfiguration (data: CameraConfigurationData): Promise<void> {
      state.saving = true
      try {
        const { data: response } = await hubApi.exampleStreams.createExampleStream(await data.toCreateBody())
        const newCameraConfiguration = CameraConfiguration.fromResponse(response)
        invariantsKeeper.onCameraConfigurationUpdate(newCameraConfiguration)
        state.unfilteredCameraConfigurations = addOrReplaceByKey(state.unfilteredCameraConfigurations, newCameraConfiguration, 'id')
      } catch (error) {
        console.error(error)
      } finally {
        state.saving = false
      }
    },

    async deleteCameraConfiguration (config: CameraConfiguration): Promise<void> {
      state.loading = true
      try {
        await hubApi.exampleStreams.deleteExampleStream(config.id, { hardDelete: true })
        state.unfilteredCameraConfigurations = state.unfilteredCameraConfigurations.filter(c => c.id !== config.id)
        invariantsKeeper.onCameraConfigurationDelete(config.id)
      } finally {
        state.loading = false
      }
    },

    async downloadCameraConfiguration (
      cameraConfigurationId?: string,
      cameraId?: string,
      configName?: string,
    ): Promise<void> {
      state.downloading = true
      try {
        if (!cameraConfigurationId || !cameraId || !configName) {
          throw new Error('Missing required parameters')
        }

        const response = await hubApi.exampleStreams.getCameraConfigData(
          cameraConfigurationId,
          cameraId,
          { format: 'arraybuffer' },
        )

        if (response.status === HttpStatusCode.NOT_FOUND) {
          throw new Error('Failed to download camera configuration')
        }

        const data = response.data
        const blob = new Blob([data], { type: 'octet/stream' })
        saveAs(blob, configName)
      } finally {
        state.downloading = false
      }
    },

    async downloadValidationLogs (
      config: CameraConfiguration,
      repeat = 0,
      repeatDelay = 1000,
    ): Promise<void> {
      state.downloading = true
      try {
        const response = await hubApi.exampleStreams.getValidationLogs(
          config.id,
          {
            format: 'arraybuffer',
            validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
          },
        )

        // check for logs every second
        if (response.status === HttpStatusCode.NOT_FOUND) {
          if (repeat) {
            await wait(repeatDelay)
            await this.downloadValidationLogs(config, repeat - 1, repeatDelay)
            return
          } else {
            throw new Error('Failed to download validation logs')
          }
        }

        const blob = new Blob([response.data], { type: 'octet/stream' })
        const date = formatDate(new Date(), DateTemplates.Long)

        const fileExtension = getValidationLogFileExtension(
          getResponseHeader(response, HttpHeaderName.ContentDisposition),
        )
        const fileName = `validation_logs_${date}.${fileExtension}`

        saveAs(blob, fileName)
      } finally {
        state.downloading = false
      }
    },

    handleCameraConfiguration (data: HubExampleStream): void {
      if (!isValidConfigKind(data.config.kind)) return

      const cameraConfiguration = CameraConfiguration.fromResponse(data)
      invariantsKeeper.onCameraConfigurationUpdate(cameraConfiguration)
      state.unfilteredCameraConfigurations = addOrReplaceByKey(state.unfilteredCameraConfigurations, cameraConfiguration, 'id')
    },

    handleDeleteCameraConfigurationEvent (deletedCameraConfiguration: HubDeletedExampleStream): void {
      state.unfilteredCameraConfigurations = state.unfilteredCameraConfigurations.filter(
        c => c.id !== deletedCameraConfiguration.id,
      )
      invariantsKeeper.onCameraConfigurationDelete(deletedCameraConfiguration.id)
    },

    async loadCameraConfiguration (id: string): Promise<void> {
      state.loading = true
      try {
        const { data } = await hubApi.exampleStreams.getExampleStream(id)

        if (!isValidConfigKind(data.config.kind)) return

        const cameraConfiguration = CameraConfiguration.fromResponse(data)

        invariantsKeeper.onCameraConfigurationUpdate(cameraConfiguration)
        state.unfilteredCameraConfigurations = addOrReplaceByKey(
          state.unfilteredCameraConfigurations,
          cameraConfiguration,
          'id',
        )
      } finally {
        state.loading = false
      }
    },

    async loadCameraConfigurationCalibrationResource (
      cameraConfigurationId: string,
      projectId: string,
    ): Promise<HubResource | undefined> {
      try {
        const { data } = await hubApi.alpha.listProjectCalibrationResourcesPerCameraConfig(
          projectId,
          cameraConfigurationId,
          undefined,
          {
            validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
          },
        )

        if (data.id === ApiErrorId.CalibrationResourcesNotFound) {
          state.cameraConfigurationsCalibrationResource.delete(cameraConfigurationId)
          return undefined
        }

        state.cameraConfigurationsCalibrationResource.set(cameraConfigurationId, data)
        return data
      } catch (e) {
        console.error(e)
      }
    },

    async loadCameraConfigurationFiles (cameraConfiguration: CameraConfiguration): Promise<void> {
      state.loading = true
      try {
        if (cameraConfiguration.config.kind !== CameraConfigurationKindIds.Camera) return
        await Promise.all((cameraConfiguration.config as CameraConfigurationCameraKind).cameras.map(async c => {
          const response = await hubApi.exampleStreams.getCameraConfigData(
            cameraConfiguration.id,
            c.id,
            { format: 'arraybuffer' },
          )
          c.configFile = new File([response.data], c.configName)
        }))

        invariantsKeeper.onCameraConfigurationUpdate(cameraConfiguration)
        state.unfilteredCameraConfigurations = addOrReplaceByKey(
          state.unfilteredCameraConfigurations,
          cameraConfiguration,
          'id',
        )
      } finally {
        state.loading = false
      }
    },

    async loadCameraConfigurations (filter: CameraConfigurationFilter[] = []): Promise<void> {
      state.loading = true
      try {
        const { data } = await hubApi.exampleStreams.listExampleStreams({
          filter,
        })
        state.lastLoadFilters = filter

        const cameraConfigurations = data.items
          .filter(c => isValidConfigKind(c.config.kind))
          .map(CameraConfiguration.fromResponse)

        invariantsKeeper.onManyCameraConfigurationsUpdate(cameraConfigurations)
        state.unfilteredCameraConfigurations = cameraConfigurations
      } finally {
        state.loading = false
      }
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },

    async startValidation (cameraConfigurationId: CameraConfiguration['id']): Promise<void> {
      try {
        const response = await hubApi.exampleStreams.revalidateExampleStream(cameraConfigurationId)
        const updatedConfiguration = CameraConfiguration.fromResponse(response.data)
        invariantsKeeper.onCameraConfigurationUpdate(updatedConfiguration)
        state.unfilteredCameraConfigurations = addOrReplaceByKey(
          state.unfilteredCameraConfigurations,
          updatedConfiguration,
          'id',
        )
      } catch (error) {
        console.error(error)
      }
    },

    async stopValidation (cameraConfigurationId: CameraConfiguration['id']): Promise<void> {
      state.pendingStopValidations.add(cameraConfigurationId)
      try {
        const response = await hubApi.exampleStreams.stopValidationExampleStream(cameraConfigurationId)
        const updatedConfiguration = CameraConfiguration.fromResponse(response.data)
        invariantsKeeper.onCameraConfigurationUpdate(updatedConfiguration)
        state.unfilteredCameraConfigurations = addOrReplaceByKey(
          state.unfilteredCameraConfigurations,
          updatedConfiguration,
          'id',
        )
      } catch (error) {
        state.pendingStopValidations.delete(cameraConfigurationId)
        console.error(error)
      }
    },

    async updateCameraConfiguration (
      id: string,
      data: CameraConfigurationData,
      originalData: CameraConfigurationData,
    ): Promise<void> {
      state.saving = true
      try {
        const { data: response } = await hubApi.exampleStreams.updateExampleStream(
          id,
          await data.toUpdateBody(originalData),
        )
        const newCameraConfiguration = CameraConfiguration.fromResponse(response)
        invariantsKeeper.onCameraConfigurationUpdate(newCameraConfiguration)
        state.unfilteredCameraConfigurations = state.unfilteredCameraConfigurations.map(c => {
          return c.id === newCameraConfiguration.parentExampleStreamId
            ? newCameraConfiguration
            : c
        })
      } catch (error) {
        console.error(error)
      } finally {
        state.saving = false
      }
    },
  }

  const getters = {
    cameraConfigurations: filteredCameraConfigurations,
    getCameraConfigurationById (cameraConfigurationId: string): CameraConfiguration | undefined {
      return state.unfilteredCameraConfigurations.find(
        cameraConfiguration => cameraConfiguration.id === cameraConfigurationId,
      )
    },
  }

  return {
    ...toRefs(shallowReadonly(state)),
    ...actions,
    ...getters,
  }
})
